import { createContext, useContext, useState, useEffect, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function fmtErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
}

export async function apiFetch(path, { method = "GET", body, auth = true } = {}) {
  const headers = { "Content-Type": "application/json" };
  const token = localStorage.getItem("arca_token");
  if (auth && token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(fmtErr(data.detail));
  return data;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=guest
  const [token, setToken] = useState(() => localStorage.getItem("arca_token"));

  const refresh = useCallback(async () => {
    const t = localStorage.getItem("arca_token");
    if (!t) { setUser(null); return; }
    try { setUser(await apiFetch("/auth/me")); }
    catch { localStorage.removeItem("arca_token"); setUser(null); setToken(null); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setAuth = (d) => { localStorage.setItem("arca_token", d.token); setToken(d.token); setUser(d.user); };
  const login = async (email, password) => setAuth(await apiFetch("/auth/login", { method: "POST", body: { email, password }, auth: false }));
  const register = async (email, password, name) => setAuth(await apiFetch("/auth/register", { method: "POST", body: { email, password, name }, auth: false }));

  // First-party Google OAuth (ArcaCentum-owned client). Redirects the browser to
  // Google directly — no third-party managed auth. On return, /auth/google
  // (GoogleCallback) exchanges the code via loginWithGoogleCode().
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginGoogle = async () => {
    const cfg = await apiFetch("/auth/google/config", { auth: false });
    if (!cfg.enabled) throw new Error("Google sign-in is being set up. Please continue with email and password for now.");
    const state = crypto.randomUUID();
    const nonce = crypto.randomUUID();
    sessionStorage.setItem("arca_g_state", state);
    sessionStorage.setItem("arca_g_nonce", nonce);
    const params = new URLSearchParams({
      client_id: cfg.client_id,
      redirect_uri: window.location.origin + "/auth/google",
      response_type: "code",
      scope: "openid email profile",
      state,
      nonce,
      prompt: "select_account",
      access_type: "online",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const loginWithGoogleCode = async ({ code, nonce }) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    setAuth(await apiFetch("/auth/google/callback", {
      method: "POST", auth: false,
      body: { code, redirect_uri: window.location.origin + "/auth/google", nonce },
    }));
  };

  const logout = () => { localStorage.removeItem("arca_token"); setToken(null); setUser(null); };

  return <AuthContext.Provider value={{ user, token, login, register, loginGoogle, loginWithGoogleCode, logout, refresh, isAuthed: !!token }}>{children}</AuthContext.Provider>;
}
